import ContentLiveForm from '@/components/pages/create-webinar/evergreen/ContentLiveForm';
import PurchaseNotificationsEditor from '@/components/pages/create-webinar/evergreen/PurchaseNotificationsEditor';
// import PdfVideoEvergreenForm from '@/components/pages/create-webinar/evergreen/PdfVideoEvergreenForm';
import OffersForm from '@/components/pages/create-webinar/live/offer/OffersForm';
import QuitForm from '@/components/pages/create-webinar/live/quit/QuitForm';
import QuizsForm from '@/components/pages/create-webinar/live/quiz/QuizsForm';
import SurveysForm from '@/components/pages/create-webinar/live/survey/SurveysForm';
import TitleLayout from '@/components/pages/create-webinar/TitleLayout';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { fetchPlayback } from '@/features/live/liveThunks';
import {
  fetchEvergreenConfiguration,
  fetchEvergreenTimeline,
  fetchQuitPage,
  fetchWebinarById,
  fetchWebinarSessionConfiguration,
} from '@/features/webinar/webinarThunks';
import SharedResourcesForm from '@/components/pages/create-webinar/live/resources/SharedResourcesForm';
import { resolveDisplayEventType } from '@/features/webinar/evergreenTimelineUtils';
import {
  canScheduleEvergreenInteractions,
  resolveEvergreenPreviewVideo,
} from '@/lib/evergreenLiveSource';
import type { TimeCodeScheduling } from '@/components/pages/create-webinar/evergreen/TimeCodeComponent';

interface EvergreenSectionProps {
  currentWebinarId?: number | null;
  isEdit?: boolean;
  onSaveSuccess?: () => void;
}

export default function EvergreenSection({
  currentWebinarId,
  isEdit,
  onSaveSuccess,
}: EvergreenSectionProps) {
  const { t } = useTranslation('createWebinar');
  const dispatch = useAppDispatch();
  const workspaceId = useAppSelector((state: RootState) => state.workspace.currentWorkspace?.id);
  const currentEvergreenConfiguration = useAppSelector(
    (state: RootState) => state.webinar.currentEvergreenConfiguration,
  );
  const currentSessionConfiguration = useAppSelector(
    (state: RootState) => state.webinar.currentSessionConfiguration,
  );
  const evergreenTimeline = useAppSelector((state: RootState) => state.webinar.evergreenTimeline);
  const playbackInfo = useAppSelector((state: RootState) => state.live.playbackInfo);
  const currentWebinarSession = useAppSelector(
    (state: RootState) => state.webinar.currentWebinarSession,
  );

  console.log('evergreen configuration', currentEvergreenConfiguration);
  console.log('evergreen timeline', evergreenTimeline);
  const { webinarId: webinarIdParam } = useParams<{ webinarId?: string }>();
  const webinarIdRaw = webinarIdParam || currentWebinarId;
  const webinarId =
    typeof webinarIdRaw === 'number' ? webinarIdRaw : parseInt(webinarIdRaw ?? '0', 10);

  useEffect(() => {
    if (!workspaceId || !webinarId || isNaN(webinarId)) return;
    dispatch(fetchWebinarById({ workspaceId, id: webinarId }));
    dispatch(fetchWebinarSessionConfiguration({ workspaceId, webinarId }));
    dispatch(fetchEvergreenConfiguration({ workspaceId, webinarId }));
    dispatch(fetchEvergreenTimeline({ workspaceId, webinarId }));
    dispatch(fetchQuitPage({ workspaceId, webinarId }));
  }, [workspaceId, webinarId, dispatch]);

  const evergreenSession = currentSessionConfiguration?.evergreenSession;
  useEffect(() => {
    if (!workspaceId || !evergreenSession?.webinarId || evergreenSession?.sessionId == null) return;
    dispatch(
      fetchPlayback({
        workspaceId,
        webinarId: evergreenSession.webinarId,
        sessionId: evergreenSession.sessionId,
      }),
    );
  }, [workspaceId, evergreenSession?.webinarId, evergreenSession?.sessionId, dispatch]);

  const isEvergreenSessionReadOnly =
    currentWebinarSession != null &&
    currentWebinarSession.webinarId === webinarId &&
    currentWebinarSession.sessionType === 'EVERGREEN' &&
    currentWebinarSession.status === 'IN_PROGRESS';

  const evergreenConfiguration =
    currentEvergreenConfiguration?.webinarId === webinarId ? currentEvergreenConfiguration : null;
  const { videoUrl, videoFallbackUrl } = resolveEvergreenPreviewVideo({
    importedVideoUrl: evergreenConfiguration?.videoUrl ?? '',
    hasSourceSession: Boolean(evergreenSession),
    playbackInfo,
  });

  const scheduling = useMemo<TimeCodeScheduling | undefined>(
    () =>
      canScheduleEvergreenInteractions(currentSessionConfiguration) && workspaceId && webinarId
        ? {
            workspaceId,
            webinarId,
            durationSeconds: evergreenConfiguration?.durationSeconds ?? null,
          }
        : undefined,
    [currentSessionConfiguration, workspaceId, webinarId, evergreenConfiguration],
  );

  const hasOfferEvent = (evergreenTimeline ?? []).some((e) => e.eventType === 'OFFER');
  const hasSurveyEvent = (evergreenTimeline ?? []).some((e) => e.eventType === 'SURVEY');
  const hasQuizEvent = (evergreenTimeline ?? []).some((e) => e.eventType === 'QUIZ');
  const hasSharedResourceEvent = (evergreenTimeline ?? []).some(
    (e) => resolveDisplayEventType(e) === 'SHARED_RESOURCE',
  );
  const hasPurchaseNotificationEvent = (evergreenTimeline ?? []).some(
    (e) => e.eventType === 'PURCHASE_NOTIFICATION',
  );

  const disabledAccordionItem = (title: string, value: string) => (
    <Accordion type="single" collapsible value="" key={value}>
      <AccordionItem value={value} className={`bg-white p-0 ${isEdit ? 'accordion-page' : ''}`}>
        <AccordionTrigger className="rounded-[20px]" disabled>
          {title}
        </AccordionTrigger>
        <AccordionContent></AccordionContent>
      </AccordionItem>
    </Accordion>
  );

  return (
    <TitleLayout
      title={t('evergreenSection.title')}
      subtitle={t('evergreenSection.subtitle')}
      isEdit={isEdit}
    >
      <div className="space-y-8">
        {/* Content Live */}
        <ContentLiveForm videoUrl={videoUrl} videoFallbackUrl={videoFallbackUrl} isEdit={isEdit} />

        {/* Pdf*/}
        {/* <PdfVideoEvergreenForm videoUrl={videoUrl} type="pdf" /> */}

        {/* Video */}
        {/* <PdfVideoEvergreenForm videoUrl={videoUrl} type="video" /> */}

        {/* Offers */}
        {hasOfferEvent || scheduling ? (
          <OffersForm
            webinarId={webinarId as number}
            workspaceId={workspaceId as number}
            isEdit={isEdit}
            isEvergreen
            videoUrl={videoUrl}
            videoFallbackUrl={videoFallbackUrl}
            scheduling={scheduling}
            readOnly={isEvergreenSessionReadOnly}
          />
        ) : (
          disabledAccordionItem(t('evergreenSection.disabled.offers'), 'offers-empty')
        )}

        {/* Surveys */}
        {hasSurveyEvent || scheduling ? (
          <SurveysForm
            webinarId={webinarId as number}
            workspaceId={workspaceId as number}
            isEdit={isEdit}
            isEvergreen
            videoUrl={videoUrl}
            videoFallbackUrl={videoFallbackUrl}
            scheduling={scheduling}
            readOnly={isEvergreenSessionReadOnly}
          />
        ) : (
          disabledAccordionItem(t('evergreenSection.disabled.surveys'), 'surveys-empty')
        )}

        {/* Quizs */}
        {hasQuizEvent || scheduling ? (
          <QuizsForm
            webinarId={webinarId as number}
            workspaceId={workspaceId as number}
            isEdit={isEdit}
            isEvergreen
            videoUrl={videoUrl}
            videoFallbackUrl={videoFallbackUrl}
            scheduling={scheduling}
            readOnly={isEvergreenSessionReadOnly}
          />
        ) : (
          disabledAccordionItem(t('evergreenSection.disabled.quiz'), 'quizs-empty')
        )}

        {/* Fichiers & Liens */}
        {hasSharedResourceEvent || scheduling ? (
          <SharedResourcesForm
            webinarId={webinarId as number}
            workspaceId={workspaceId as number}
            isEdit={isEdit}
            isEvergreen
            videoUrl={videoUrl}
            videoFallbackUrl={videoFallbackUrl}
            scheduling={scheduling}
            readOnly={isEvergreenSessionReadOnly}
          />
        ) : (
          disabledAccordionItem(
            t('evergreenSection.disabled.sharedResources'),
            'shared-resources-empty',
          )
        )}

        {(hasPurchaseNotificationEvent || scheduling) && workspaceId && webinarId && (
          <PurchaseNotificationsEditor
            workspaceId={workspaceId}
            webinarId={webinarId}
            videoDurationSeconds={evergreenConfiguration?.durationSeconds ?? null}
            readOnly={isEvergreenSessionReadOnly}
            isEdit={isEdit}
          />
        )}

        {/* Quit */}
        <QuitForm
          webinarId={webinarId as number}
          workspaceId={workspaceId as number}
          isEdit={isEdit}
          isEvergreen
          readOnly={isEvergreenSessionReadOnly}
          onSaveSuccess={onSaveSuccess}
        />
      </div>
    </TitleLayout>
  );
}
